import { useEffect, useRef, useState } from "react";
import FormDropDown from "../../../forms/DropDownList/FormDropDown";
import "./AnswerSelector.scss";
import ToggleCheck from "../../../forms/ToggleCheck/ToggleCheck";
import Toggle from "../../../forms/Toggle/Toggle";
import axios from "../../../../api/axios";
import { getPollRunsRoute, updatePollRunCorrectAnswerRoute, updatePollRunLeaderboardExclusionRoute } from "../../../../api/routes";

const LivePollAnswerSelector = (props) => {
    const pollsData = props.customisationData?.setup?.pollData?.polls;
    const featureVars = props.featureVars;
    const selectedPollIndex = featureVars?.pollControl?.selectedPoll;
    const selectedPollData = pollsData?.[selectedPollIndex];
    const pollType = selectedPollData?.type;

    const [forceUpdate, setForceUpdate] = useState(0);
    const [pollRuns, setPollRuns] = useState([]);
    const [showPollData, setShowPollData] = useState(false);
    const [openRunIds, setOpenRunIds] = useState([]);
    const [voterSearches, setVoterSearches] = useState({});
    const [visibleVoterCounts, setVisibleVoterCounts] = useState({});
    const [updatingRunIds, setUpdatingRunIds] = useState([]);
    const [updatingCorrectAnswerRunIds, setUpdatingCorrectAnswerRunIds] = useState([]);
    const [pollDataError, setPollDataError] = useState('');
    useEffect(() => {
        if (!showPollData) return;
        let cancelled = false;
        const refreshChangedRun = async ({ detail }) => {
            if (String(detail.streamID) !== String(props.streamId) || String(detail.featureID) !== String(props.featureId)) return;
            try {
                const response = await axios.get(`${getPollRunsRoute}/${encodeURIComponent(detail.runId)}`, { params: { streamID: props.streamId } });
                if (cancelled) return;
                const saved = response.data?.pollRun;
                if (!saved) return;
                setPollRuns(runs => runs.map(item => String(item._id) === String(saved._id) && Number(saved.__v || 0) >= Number(item.__v || 0) ? saved : item));
                // Keep expanded rounds, searches and other local UI state intact.
            } catch (error) {
                if (!cancelled) setPollDataError('Another producer updated this poll, but the latest result could not be loaded. Reopen Poll Data to retry.');
            }
        };
        window.addEventListener('dashboard:poll-run-changed', refreshChangedRun);
        return () => { cancelled = true; window.removeEventListener('dashboard:poll-run-changed', refreshChangedRun); };
    }, [showPollData, props.streamId, props.featureId]);
    const forceUpdateNow = () => {
        setForceUpdate((val) => val + 1);
    }

    const loadPollData = async (pollTitle = selectedPollData?.title, requestedRunId = '') => {
        if (!props.streamId || !pollTitle) return;
        const response = await axios.get(getPollRunsRoute, { params: { streamID: props.streamId } });
        const runs = (response.data?.pollRuns || []).filter((run) =>
            String(run.featureID) === String(props.featureId) && run.pollTitle === pollTitle
        );
        setPollRuns(runs);
        const requestedRun = runs.find((run) => String(run._id) === String(requestedRunId));
        setOpenRunIds(requestedRun ? [String(requestedRun._id)] : runs.length === 1 ? [String(runs[0]._id)] : []);
        setShowPollData(true);
    };

    useEffect(() => {
        const openRequestedPoll = ({ detail }) => {
            if (String(detail?.streamId) !== String(props.streamId) || String(detail?.featureId) !== String(props.featureId)) return;
            loadPollData(detail.pollTitle, detail.runId).catch(() => setPollDataError('Unable to load saved poll data.'));
        };
        window.addEventListener('dashboard:open-poll-data', openRequestedPoll);
        return () => window.removeEventListener('dashboard:open-poll-data', openRequestedPoll);
    }, [props.streamId, props.featureId, selectedPollData]);

    const setRunLeaderboardExcluded = async (run, excluded) => {
        if (!props.streamId || !run?._id) return;
        const runId = String(run._id);
        setUpdatingRunIds((ids) => [...ids, runId]);
        setPollDataError('');
        // Reflect the selection immediately. If the server rejects it, the
        // catch below restores the original state and explains why.
        setPollRuns((runs) => runs.map((item) => String(item._id) === runId ? { ...item, leaderboardExcluded: excluded } : item));
        try {
            const response = await axios.patch(
                `${updatePollRunLeaderboardExclusionRoute}/${encodeURIComponent(run._id)}/leaderboard-exclusion`,
                { streamID: props.streamId, excluded },
            );
            const savedRun = response.data?.pollRun;
            setPollRuns((runs) => runs.map((item) => String(item._id) === runId && Number(savedRun?.__v || 0) >= Number(item.__v || 0) ? (savedRun || { ...item, leaderboardExcluded: excluded }) : item));
            // One round's exclusion applies to every projection. Rebuilding the
            // linked board immediately removes/restores its awards everywhere.
            try {
                await props.rebuildLivePollLeaderboardFunc?.(props.featureId);
            } catch (error) {
                // The toggle is already saved. A stream may simply not have a
                // linked leaderboard, so do not present this as a failed toggle.
                setPollDataError(error?.message || 'Round saved, but its linked leaderboard could not be refreshed.');
            }
        } catch (error) {
            setPollRuns((runs) => runs.map((item) => String(item._id) === runId && Number(item.__v || 0) <= Number(run.__v || 0) ? { ...item, leaderboardExcluded: run.leaderboardExcluded } : item));
            setPollDataError(error?.response?.data?.message || error?.message || 'Unable to update this round’s leaderboard setting.');
        } finally {
            setUpdatingRunIds((ids) => ids.filter((id) => id !== runId));
        }
    };

    const setRunCorrectAnswer = async (run, correctAnswerIndex) => {
        if (!props.streamId || !run?._id || run.correctAnswerIndex === correctAnswerIndex) return;
        const runId = String(run._id);
        setUpdatingCorrectAnswerRunIds((ids) => [...ids, runId]);
        setPollDataError('');
        try {
            const response = await axios.patch(
                `${updatePollRunCorrectAnswerRoute}/${encodeURIComponent(run._id)}/correct-answer`,
                { streamID: props.streamId, correctAnswerIndex },
            );
            const savedRun = response.data?.pollRun;
            setPollRuns((runs) => runs.map((item) => String(item._id) === runId && Number(savedRun?.__v || 0) >= Number(item.__v || 0) ? (savedRun || { ...item, correctAnswerIndex }) : item));
            try {
                await props.rebuildLivePollLeaderboardFunc?.(props.featureId);
            } catch (error) {
                setPollDataError(error?.message || 'Correct answer saved, but its linked leaderboard could not be refreshed.');
            }
        } catch (error) {
            setPollDataError(error?.response?.data?.message || error?.message || 'Unable to correct this round’s answer.');
        } finally {
            setUpdatingCorrectAnswerRunIds((ids) => ids.filter((id) => id !== runId));
        }
    };

    const resetCorrectAnswers = () => {
        const pollStateData = findPollsStateData(selectedPollData);
        if (!pollStateData?.answerState) return;
        for (let i = 0; i < pollStateData.answerState.length; i++) {
            pollStateData.answerState[i].correct = false;
        }
    }

    const findPollsStateData = (pollData) => {
        if (!pollData) {
            return null;
        }
        let pollStateData = null;
        // console.log('--- ANSWER_SELECTOR, findPollsStateData: ', props.previewControls, props.passedInPreviewData);
        if (props.previewControls && props.passedInPreviewData?.pollControl?.pollsState) {
            const pollTitle = pollData.title;
            for (let i = 0; i < props.passedInPreviewData.pollControl.pollsState.length; i++) {
                if (props.passedInPreviewData.pollControl.pollsState[i].pollTitle === pollTitle) {
                    pollStateData = props.passedInPreviewData.pollControl.pollsState[i];
                }
            }
        }
        if (pollStateData === null && featureVars?.pollControl?.pollsState) {
            const pollTitle = pollData.title;
            for (let i = 0; i < featureVars.pollControl.pollsState.length; i++) {
                if (featureVars.pollControl.pollsState[i].pollTitle === pollTitle) {
                    pollStateData = featureVars.pollControl.pollsState[i];
                }
            }
        }
        if (pollStateData === null) {
            if (!featureVars?.pollControl) return null;
            if (typeof featureVars.pollControl.pollsState === 'undefined') {
                featureVars.pollControl.pollsState = [];
            }
            pollStateData = {
                pollTitle: pollData.title,
                pollRunning: false,
                answerState: []
            };
            featureVars.pollControl.pollsState.push(pollStateData);
        }
        pollStateData.type = pollData.type;

        if (Array.isArray(pollStateData.answerState) === false) {
            pollStateData.answerState = [];
        }
        // Make sure we have state data for each answer
        // State data is in the following format:
        // {
        //     included: [boolean],
        //     correct: [boolean],
        //     votePercentage: [integer]
        // }
        // We can report the state back from the poll to keep the votePercentage up to date, although prob only at the of voting start and end
        for (let i = 0; i < 10; i++) {
            if (pollData['answer' + (i + 1)] && pollData['answer' + (i + 1)] !== '') {
                if (typeof pollStateData.answerState[i] === 'undefined') {
                    pollStateData.answerState.push({
                        included: true,
                        correct: false,
                        votePercentage: 0
                    });
                }
            } else {
                // A blank / non existent answer means we don't have any more answers
                break;
            }
        }

        return pollStateData;
    }

    const pollStateData = findPollsStateData(selectedPollData);
    const passedInPreviewData = props?.passedInPreviewData;
    const changeValFunc = props.changeValFunc;
    const reportCustomisationVarsChanged = props.reportCustomisationVarsChanged;

    useEffect(() => {
        const varsToUse = passedInPreviewData ? passedInPreviewData : featureVars;
        if (varsToUse?.pollControl?.pollsState) {
            for (let i = 0; i < varsToUse.pollControl.pollsState.length; i++) {
                const pollStateData_i = varsToUse.pollControl.pollsState[i];
                
                if (pollStateData_i && pollStateData_i.pollRunning === true) {
                    // Set a flag to show we have not reported our data yet, we only save the data at the end of the poll
                    // console.log('--- ANSWER_SELECTOR, useEffect (Reset pollRunDataSaved): ', pollStateData_i);
                    pollStateData_i.pollRunDataSaved = false;
                }

                if (pollStateData_i && pollStateData_i.pollRunning === false && pollStateData_i.pollHasVotes === true) {
                    // Set pollStateData.highlightRemovedAnswers to true ready for when we hit send / show the previous poll data
                    // console.log('--- ANSWER_SELECTOR, useEffect (Time to highlight removed answers): ', pollStateData_i);
                    pollStateData_i.highlightRemovedAnswers = true;

                    // New poll runs carry a unique run id and are persisted by
                    // the feature parent to the stream-scoped PollRun collection.
                    // Do not copy those results into campaign customisation
                    // history (which is title-keyed and overwrites reruns).
                    if (!pollStateData_i.clientRunId && pollStateData_i.pollRunDataSaved === false) {
                        // Save the poll results to customisationData
                        if (pollStateData_i.type !== 'predictive' || pollStateData_i.correctAnswer !== -1) {
                            // console.log('--- ANSWER_SELECTOR, useEffect (Time to save data): ', pollStateData_i, props.customisationData);
                            pollStateData_i.pollRunDataSaved = true;

                            // Save the poll results to customisationData
                            if (!props.isPreview && reportCustomisationVarsChanged) {
                                // No saving of runs in preview mode!
                                const pollsData = props.customisationData?.setup?.pollData?.polls;
                                if (pollsData) {
                                    let completedPollData = null;
                                    for (let i = 0; i < pollsData.length; i++) {
                                        if (pollsData[i].title === pollStateData_i.pollTitle) {
                                            completedPollData = pollsData[i];
                                        }
                                    }
                                    if (completedPollData) {
                                        // console.log('--- ANSWER_SELECTOR, useEffect (Completed poll data): ', completedPollData);
                                        let runData = null;
                                        let addNewRun = true;
                                        for (let j = 0; j < props.customisationData.setup.runData.polls.length; j++) {
                                            if (props.customisationData.setup.runData.polls[j].title === pollStateData_i.pollTitle && props.customisationData.setup.runData.polls[j].timeAndDate === pollStateData_i.startTime) {
                                                runData = props.customisationData.setup.runData.polls[j];
                                                addNewRun = false;
                                            }
                                        }
                                        if (addNewRun) {
                                            let channelString = '';
                                            if (props.channels) {
                                                if (Array.isArray(props.channels)) {
                                                    for (let i = 0; i < props.channels.length; i++) {
                                                        if (channelString !== '') {
                                                            channelString += ', ';
                                                        }
                                                        channelString += props.channels[i].twitchHandle;
                                                    }
                                                }
                                            }
                                            if (channelString === '') {
                                                channelString = 'none';
                                            }
                                            runData = {
                                                title: pollStateData_i.pollTitle,
                                                timeAndDate: pollStateData_i.startTime,
                                                channel: channelString,
                                                type: pollStateData_i.type,
                                                removalType: completedPollData.removalType,
                                                question: completedPollData.question,
                                            };
                                            props.customisationData.setup.runData.polls.push(runData);

                                            // Record our run data!
                                            let totalVotes = 0;
                                            let mostVotedVotes = 0;
                                            let leastVotes = 1000000;
                                            for (let i = 1; i <= 10; i++) {
                                                runData['answer' + i] = completedPollData['answer' + i];
                                                runData['included' + i] = pollStateData_i.answerState?.[i - 1]?.includedInLastVote ?? false;
                                                runData['votes' + i] = pollStateData_i.rawAnswerVoteCounts?.[i - 1] ?? 0;
                                                runData['percentage' + i] = pollStateData_i.answerState?.[i - 1]?.votePercentage ?? 0;
                                                totalVotes += pollStateData_i.rawAnswerVoteCounts?.[i - 1] ?? 0;
                                                if (pollStateData_i.rawAnswerVoteCounts?.[i - 1] > mostVotedVotes) {
                                                    mostVotedVotes = pollStateData_i.rawAnswerVoteCounts?.[i - 1];
                                                }
                                                if (pollStateData_i.rawAnswerVoteCounts?.[i - 1] < leastVotes) {
                                                    leastVotes = pollStateData_i.rawAnswerVoteCounts?.[i - 1];
                                                }
                                                runData['winner' + i] = false;
                                                if (pollStateData_i.type === 'removal') {
                                                    if (pollStateData_i.answerState?.[i - 1]?.includedInLastVote) {
                                                        runData['winner' + i] = !pollStateData_i.answerState?.[i - 1]?.included ?? false;
                                                    } else {
                                                        runData['winner' + i] = false;
                                                    }
                                                } else
                                                if (pollStateData_i.type === 'predictive') {
                                                    runData['winner' + i] = pollStateData_i.answerState?.[i - 1]?.correct ?? false;
                                                }
                                            }
                                            runData.totalVotes = totalVotes;
                                            let mostVotedAnswer = '';
                                            for (let i = 1; i <= 10; i++) {
                                                if (pollStateData_i.rawAnswerVoteCounts?.[i - 1] === mostVotedVotes) {
                                                    if (mostVotedAnswer !== '') {
                                                        mostVotedAnswer += ', ';
                                                    }
                                                    mostVotedAnswer += completedPollData['answer' + i];
                                                    if (pollStateData_i.type === 'opinion') {
                                                        runData['winner' + i] = true;
                                                    }
                                                }
                                            }
                                            runData.winningAnswer = mostVotedAnswer;

                                            let cumulativeRunData = null;
                                            for (let j = 0; j < props.customisationData.setup.totalData.polls.length; j++) {
                                                if (props.customisationData.setup.totalData.polls[j].title === pollStateData_i.pollTitle && props.customisationData.setup.totalData.polls[j].type === pollStateData_i.type) {
                                                    cumulativeRunData = props.customisationData.setup.totalData.polls[j];
                                                }
                                            }
                                            if (!cumulativeRunData) {
                                                cumulativeRunData = {
                                                    title: pollStateData_i.pollTitle,
                                                    type: pollStateData_i.type,
                                                    removalType: completedPollData.removalType,
                                                    question: completedPollData.question,
                                                }
                                                props.customisationData.setup.totalData.polls.push(cumulativeRunData);
                                            }
                                            cumulativeRunData.totalVotes = (cumulativeRunData.totalVotes || 0) + totalVotes;
                                            cumulativeRunData.runCount = (cumulativeRunData.runCount || 0) + 1;
                                            cumulativeRunData.lastRun = runData.timeAndDate;
                                            // copy answers from runData, update total votes
                                            for (let i = 1; i <= 10; i++) {
                                                cumulativeRunData['answer' + i] = runData['answer' + i];
                                                cumulativeRunData['votes' + i] = (cumulativeRunData['votes' + i] || 0) + runData['votes' + i];
                                                cumulativeRunData['percentage' + i] = Math.round((cumulativeRunData['votes' + i] / cumulativeRunData.totalVotes) * 100);
                                            }
                                            // Find most popular answer
                                            let mostPopularAnswer = '';
                                            let mostPopularVotes = 0;
                                            for (let i = 1; i <= 10; i++) {
                                                if (cumulativeRunData['votes' + i] > mostPopularVotes) {
                                                    mostPopularVotes = cumulativeRunData['votes' + i];
                                                }
                                            }
                                            for (let i = 1; i <= 10; i++) {
                                                if (cumulativeRunData['votes' + i] === mostPopularVotes) {
                                                    if (mostPopularAnswer !== '') {
                                                        mostPopularAnswer += ', ';
                                                    }
                                                    mostPopularAnswer += cumulativeRunData['answer' + i];
                                                }
                                            }
                                            cumulativeRunData.winningAnswer = mostPopularAnswer;

                                            // console.log('--- ANSWER_SELECTOR, useEffect (Saving run data): ', runData, cumulativeRunData);

                                            reportCustomisationVarsChanged(props.customisationVars, props.featureId);
                                        }
                                    }
                                }
                            } /* else {
                                console.log('--- ANSWER_SELECTOR, useEffect (no saving run data in preview mode): ', pollStateData_i);
                            } */
                        }
                    }
                }

                if (pollStateData && pollStateData_i && pollStateData.pollTitle === pollStateData_i.pollTitle) {
                    let pollHasVotes = false;
                    let correctAnswer = -1;
                    for (let i = 0; i < (pollStateData_i.answerState || []).length; i++) {
                        if (pollStateData_i.answerState[i].votePercentage > 0) {
                            pollHasVotes = true;
                        }
                        if (pollStateData_i.answerState[i].correct) {
                            correctAnswer = i;
                        }
                    }

                    // make sure top level, ie. varsToUse.pollControl.pollHasVotes is updated
                    if (typeof pollStateData.pollHasVotes !== 'undefined' && pollStateData.pollHasVotes !== pollHasVotes) {
                        // console.log('--- ANSWER_SELECTOR, Set pollHasVotes to: ', pollHasVotes);
                        setTimeout(() => changeValFunc('pollControl', 'pollHasVotes', pollHasVotes, false, false, pollStateData.correctAnswer !== correctAnswer ? false : true), 20);
                    }
                    // make sure top level, ie. varsToUse.pollControl.correctAnswer is updated
                    if (typeof pollStateData.correctAnswer !== 'undefined' && pollStateData.correctAnswer !== correctAnswer) {
                        // console.log('--- ANSWER_SELECTOR, Set correctAnswer to: ', correctAnswer);
                        setTimeout(() => changeValFunc('pollControl', 'correctAnswer', correctAnswer, false, false, true), 20);
                    }  
                }
            }
        }
    }, [pollStateData, featureVars, passedInPreviewData, changeValFunc]);

    return (
        <div className="fl-column live-poll-answers-selector">
            {!pollStateData &&
                <div className="form-field-content label">
                    Poll Status and Answers Not Available
                </div>
            }
            {pollStateData &&
                <>
                    <div className="fl-column no-gap">
                        {pollType === 'opinion' &&
                            <>
                                <div className="poll-type">Opinion Poll.</div>
                                <div>Results will appear below after the voting ends.</div>
                            </>
                        }
                        {pollType === 'predictive' &&
                            <>
                                <div className="poll-type">Predictive Poll.</div>
                                <div>Select the correct answer after voting ends to award points to successful voters.</div>
                            </>
                        }
                        {pollType === 'removal' &&
                            <>
                                <div className="poll-type">Removal Poll.</div>
                                <div>You will be able to select the answers to include / remove when voting is not active. Only these answers will be included the next time you run this poll.</div>
                            </>
                        }
                        {pollStateData.pollRunning &&
                            <div className="poll-running">Poll is currently running...</div>
                        }
                        {!pollStateData.pollRunning && pollType !== 'predictive' &&
                            <div className="poll-not-running">Poll is not currently running.</div>
                        }
                    </div>
                    <div className="live-poll-question-answers-panel">
                    <div>Question: <span className='question-text'>{selectedPollData.question}</span></div>
                    {pollStateData.answerState.map((answer, index) => {
                        let toggleEnabled = !pollStateData.pollRunning;
                        if (toggleEnabled && pollType === 'predictive') {
                            if (pollStateData.correctAnswer !== -1 || !pollStateData.pollHasVotes) {
                                toggleEnabled = false;
                            }
                        }
                        return (
                            <div className="fl-row compact" key={index}>
                                {(pollType === 'predictive' || pollType === 'removal') &&
                                    <div className="shrink">
                                        <ToggleCheck
                                            label={selectedPollData['answer' + (index + 1)]}
                                            showLabel={false}
                                            currentState={pollType === 'predictive' ? answer.correct : answer.included}
                                            toggleFunc={(e, id) => {
                                                if (pollType === 'predictive') {
                                                    const correct = !answer.correct;
                                                    resetCorrectAnswers();
                                                    answer.correct = correct;
                                                } else {
                                                    answer.included = !answer.included;
                                                }
                                                forceUpdateNow();
                                                // props.setFeatureVars(featureVars);
                                            }}
                                            enabled={toggleEnabled}
                                        />
                                    </div>
                                }
                                <div className="grow answer-details">
                                    <div className="answer-text">
                                        {selectedPollData['answer' + (index + 1)]}
                                    </div>
                                    {pollType === 'predictive' &&
                                        <div className="answer-points">
                                            {Number(selectedPollData['points' + (index + 1)] || 0)} pts
                                        </div>
                                    }
                                </div>
                                <div className="shrink vote-percentage">
                                    {answer.votePercentage}%
                                </div>
                            </div>
                        )
                    })}
                    {pollType === 'predictive' && <>
                        <button type="button" className="standard-button live-but-purple" disabled={pollStateData.pollRunning || !pollStateData.pollHasVotes || Number(pollStateData.correctAnswer) >= 0} onClick={!pollStateData.pollRunning && pollStateData.pollHasVotes && Number(pollStateData.correctAnswer) < 0 ? (event) => props.sendFeatureCommandFunc(event, 'resolveLivePoll', props.featureId, null) : null}>
                            Set Correct Answer
                        </button>
                        {!pollStateData.pollRunning && <div className="poll-not-running">Poll is not currently running.</div>}
                    </>}
                    </div>
                </>
            }
            {showPollData &&
                <div className="live-poll-data-modal" role="dialog" aria-modal="true" aria-label="Saved poll data">
                    <div className="live-poll-data-modal__panel">
                        <div className="live-poll-data-modal__heading">
                            <div><strong>{selectedPollData?.title}</strong><br /><span>Saved poll runs</span></div>
                            <button type="button" onClick={() => setShowPollData(false)} aria-label="Close poll data">×</button>
                        </div>
                        {pollRuns.length === 0 && <p>No saved runs were found for this poll in this stream.</p>}
                        {pollDataError && <p className="live-poll-data-modal__error">{pollDataError}</p>}
                        {pollRuns.map((run) => {
                            const runId = String(run._id);
                            const isOpen = openRunIds.includes(runId);
                            const totalVotes = (run.votes || []).length;
                            const isUpdating = updatingRunIds.includes(runId);
                            const isCorrectAnswerUpdating = updatingCorrectAnswerRunIds.includes(runId);
                            return <div className={`live-poll-data-run${run.leaderboardExcluded ? ' is-leaderboard-excluded' : ''}`} key={runId}>
                                <button type="button" className="live-poll-data-run__toggle" onClick={() => setOpenRunIds((ids) => isOpen ? ids.filter((id) => id !== runId) : [...ids, runId])}>
                                    <span>{isOpen ? '−' : '+'} {new Date(run.completedAt).toLocaleString()} · {totalVotes} vote{totalVotes === 1 ? '' : 's'} · {run.status}</span>
                                </button>
                                {isOpen && <div className="live-poll-data-run__body">
                                    <div className="live-poll-data-run__actions">
                                        {run.status === 'resolved' && <div className="live-poll-data-run__exclude">
                                            <span>Exclude from leaderboards</span>
                                            <Toggle
                                                id={`exclude-poll-run-${runId}`}
                                                currentState={Boolean(run.leaderboardExcluded)}
                                                toggleFunc={() => setRunLeaderboardExcluded(run, !run.leaderboardExcluded)}
                                                enabled={!isUpdating}
                                                variant="default"
                                            />
                                        </div>}
                                        {props.showLivePollRunFunc && <button
                                            type="button"
                                            className="standard-button live-poll-data-run__show-result"
                                            onClick={() => props.showLivePollRunFunc(props.featureId, run)}
                                        >
                                            Show this round’s results
                                        </button>}
                                    </div>
                                    <div><strong>Question:</strong> {run.pollSnapshot?.question}</div>
                                    {(run.pollSnapshot?.answers || []).map((answer, index) => {
                                        const total = (run.totals || []).find((item) => item.answerIndex === index) || {};
                                        const winner = run.correctAnswerIndex === index;
                                        const voters = (run.votes || []).filter((vote) => vote.answerIndex === index);
                                        const voterKey = `${runId}-${index}`;
                                        const search = voterSearches[voterKey] || '';
                                        const filteredVoters = voters.filter((vote) => vote.username.toLowerCase().includes(search.toLowerCase()));
                                        const pageSize = 25;
                                        const page = visibleVoterCounts[voterKey] || 0;
                                        const pageCount = Math.max(1, Math.ceil(filteredVoters.length / pageSize));
                                        const safePage = Math.min(page, pageCount - 1);
                                        const pageStart = safePage * pageSize;
                                        return <div className={`live-poll-data-answer${winner ? ' is-winner' : ''}`} key={index}>
                                            <div className="live-poll-data-answer__summary">
                                                <span>{answer.text}{winner ? ' — correct answer' : ''}{run.pollSnapshot?.type === 'removal' && typeof answer.included === 'boolean' ? (answer.included ? ' — kept' : ' — removed') : ''}</span>
                                                <span>{Number(total.votes || 0)} votes · {Number(total.percentage || 0)}%{answer.points ? ` · ${answer.points} pts` : ''}</span>
                                            </div>
                                            {run.pollSnapshot?.type === 'predictive' && run.status === 'resolved' && <button
                                                type="button"
                                                className={`live-poll-data-answer__correct-button${winner ? ' is-current' : ''}`}
                                                disabled={winner || isCorrectAnswerUpdating}
                                                onClick={() => setRunCorrectAnswer(run, index)}
                                            >
                                                {winner ? 'Correct answer' : 'Set as correct answer'}
                                            </button>}
                                            {voters.length > 0 &&
                                                <details className="live-poll-data-voters">
                                                    <summary>{voters.length} player{voters.length === 1 ? '' : 's'} chose this answer</summary>
                                                    <input
                                                        className="live-poll-data-voters__search"
                                                        type="search"
                                                        value={search}
                                                        placeholder="Find a player"
                                                        onChange={(event) => {
                                                            setVoterSearches((searches) => ({ ...searches, [voterKey]: event.target.value }));
                                                            setVisibleVoterCounts((counts) => ({ ...counts, [voterKey]: 0 }));
                                                        }}
                                                    />
                                                    <div className="live-poll-data-voters__list">
                                                        {filteredVoters.slice(pageStart, pageStart + pageSize).map((vote) => <a key={`${vote.playerId}-${vote.username}`} href={`https://www.twitch.tv/${encodeURIComponent(vote.username)}`} target="_blank" rel="noreferrer">{vote.username}</a>)}
                                                    </div>
                                                    {filteredVoters.length === 0 && <div className="live-poll-data-voters__empty">No matching players.</div>}
                                                    {filteredVoters.length > 0 && <div className="live-poll-data-voters__pager">
                                                        <span>Showing {pageStart + 1}–{Math.min(pageStart + pageSize, filteredVoters.length)} of {filteredVoters.length}</span>
                                                        <button type="button" disabled={safePage === 0} onClick={() => setVisibleVoterCounts((counts) => ({ ...counts, [voterKey]: safePage - 1 }))}>Previous</button>
                                                        <span>Page {safePage + 1} / {pageCount}</span>
                                                        <button type="button" disabled={safePage >= pageCount - 1} onClick={() => setVisibleVoterCounts((counts) => ({ ...counts, [voterKey]: safePage + 1 }))}>Next</button>
                                                    </div>}
                                                </details>
                                            }
                                        </div>;
                                    })}
                                    {run.status === 'resolved' && <div><strong>Awards:</strong> {(run.awards || []).length} correct player{(run.awards || []).length === 1 ? '' : 's'}</div>}
                                </div>}
                            </div>;
                        })}
                    </div>
                </div>
            }
        </div>
    )
}

export default LivePollAnswerSelector;
